import { type ClassValue, clsx } from 'clsx';
import { twMerge } from 'tailwind-merge';
import { loadStripe } from '@stripe/stripe-js';

export const stripePromise = loadStripe(import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY);

export const STRIPE_PRICE_IDS = {
  pro_builder_monthly: 'handled_by_edge_function',
  founding_candidate_yearly: 'handled_by_edge_function',
};

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export type UserTier = {
  name: string;
  color: string;
  minChallenges: number;
  effect?: string;
};

export const USER_TIERS: UserTier[] = [
  {
    name: 'Code Breaker',
    color: 'text-white',
    minChallenges: 0,
    effect: 'codebreaker-effect'
  },
  {
    name: 'Code Specialist',
    color: 'text-emerald-500',
    minChallenges: 5,
    effect: 'specialist-effect'
  },
  {
    name: 'Elite Developer',
    color: 'text-yellow-500',
    minChallenges: 10,
    effect: 'elite-effect'
  },
  {
    name: 'Cyber Architect',
    color: 'text-orange-500',
    minChallenges: 15,
    effect: 'cyber-effect'
  },
  {
    name: 'System Overlord',
    color: 'text-red-500',
    minChallenges: 25,
    effect: 'overlord-effect'
  },
  {
    name: 'The Compiler',
    color: 'text-purple-500',
    minChallenges: 40,
    effect: 'wizard-effect'
  },
  {
    name: 'The Omniscient',
    color: 'text-white',
    minChallenges: 70,
    effect: 'glitch-effect'
  },
  {
    name: 'Founding Candidate',
    color: 'text-yellow-400',
    minChallenges: 0,
    effect: 'founding-candidate-effect'
  },
   {
    name: 'System Admin',
    color: 'text-cyan-400',
    minChallenges: 999,
    effect: 'system-admin-effect'
  }
];

export type EmployerTier = {
  name: string;
  color: string;
  effect?: string;
};

export const EMPLOYER_TIERS: EmployerTier[] = [
  {
    name: 'Magnificent Seven',
    color: 'text-purple-500',
    effect: 'codebreaker-effect'
  },
  {
    name: 'IPO-Ready',
    color: 'text-green-500',
    effect: 'codebreaker-effect'
  },
  {
    name: 'Two-Pizza Team',
    color: 'text-yellow-500',
    effect: 'codebreaker-effect'
  },
  {
    name: 'VC-Backed Rocketship',
    color: 'text-red-500',
    effect: 'codebreaker-effect'
  },
  {
    name: 'Big 4',
    color: 'text-blue-500',
    effect: 'codebreaker-effect'
  },
  {
    name: 'Tech Titan',
    color: 'text-neutral-500',
    effect: 'codebreaker-effect'
  },
  {
    name: 'Talent Magnet',
    color: 'text-pink-500',
    effect: 'codebreaker-effect'
  },
  {
    name: 'Sleeping Giant',
    color: 'text-teal-500',
    effect: 'codebreaker-effect'
  },
  {
    name: 'Open Source Luminary',
    color: 'text-amber-500',
    effect: 'codebreaker-effect'
  },
  {
    name: 'AI Vanguard',
    color: 'text-violet-500',
    effect: 'codebreaker-effect'
  },
  {
    name: 'Fortune 100',
    color: 'text-gray-500',
    effect: 'codebreaker-effect'
  },
  {
    name: 'Fortune 500',
    color: 'text-gray-400',
    effect: 'codebreaker-effect'
  },
  {
    name: 'Remote-First Leader',
    color: 'text-blue-400',
    effect: 'codebreaker-effect'
  },
  {
    name: 'Rocket Propelled',
    color: 'text-red-400',
    effect: 'codebreaker-effect'
  },
  {
    name: 'Orbital Innovator',
    color: 'text-purple-400',
    effect: 'codebreaker-effect'
  },
  {
    name: 'GovTech Specialist',
    color: 'text-blue-500',
    effect: 'codebreaker-effect'
  },
  {
    name: 'Fintech Powerhouse',
    color: 'text-green-500',
    effect: 'codebreaker-effect'
  },
  {
    name: 'Garage-Hero',
    color: 'text-amber-500',
    effect: 'codebreaker-effect'
  },
  {
    name: 'Lifestyle Leader',
    color: 'text-teal-400',
    effect: 'codebreaker-effect'
  }
];

export function getUserTier(completedChallengesCount: number): UserTier {
  const regularTiers = USER_TIERS.filter(tier => tier.name !== 'Founding Candidate');
  return [...regularTiers]
    .reverse()
    .find(tier => completedChallengesCount >= tier.minChallenges) || regularTiers[0];
}

export function initializeTierEffects() {
  if (typeof window === 'undefined') return;
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', setupEffects);
  } else {
    setupEffects();
  }
  const observer = new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      if (mutation.type === 'childList') {
        setupEffects();
      }
    });
  });
  observer.observe(document.body, {
    childList: true,
    subtree: true
  });
}

function setupEffects() {
  setupCyberEffect();
}

function setupCyberEffect() {
  const cyberElements = document.querySelectorAll('.cyber-effect');
  cyberElements.forEach(element => {
    if (element.getAttribute('data-interval-set') === 'true') return;
    const text = element.textContent?.trim() || '';
    element.setAttribute('data-text', text);
    element.setAttribute('data-interval-set', 'true');
    element.innerHTML = '';
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789@#$%&*<>[]{}|';
    [...text].forEach((letter, index) => {
      const span = document.createElement('span');
      span.className = 'cyber-letter';
      span.textContent = letter;
      span.style.animationDelay = `${index * 0.1}s`;
      span.setAttribute('data-final', letter);
      span.setAttribute('data-random', chars[Math.floor(Math.random() * chars.length)]);
      element.appendChild(span);
    });
    setTimeout(() => {
      triggerScrambleEffect(element as HTMLElement);
    }, 100);
    const intervalId = setInterval(() => {
      triggerScrambleEffect(element as HTMLElement);
    }, 3500);
    element.setAttribute('data-interval-id', intervalId.toString());
  });
}

function triggerScrambleEffect(element: HTMLElement) {
  const letters = element.querySelectorAll('.cyber-letter');
  letters.forEach((letter, idx) => {
    setTimeout(() => {
      resetLetterAnimation(letter as HTMLElement);
    }, idx * 50);
  });
}

function resetLetterAnimation(letterElement: HTMLElement) {
  const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789@#$%&*<>[]{}|';
  const finalChar = letterElement.getAttribute('data-final') || '';
  const r1 = chars[Math.floor(Math.random() * chars.length)];
  const r2 = chars[Math.floor(Math.random() * chars.length)];
  const r3 = chars[Math.floor(Math.random() * chars.length)];
  letterElement.style.animation = 'none';
  letterElement.offsetHeight;
  letterElement.style.animation = 'scrambleIn 0.8s forwards';
  letterElement.textContent = r1;
  setTimeout(() => {
    letterElement.textContent = r2;
  }, 100);
  setTimeout(() => {
    letterElement.textContent = r3;
  }, 200);
  setTimeout(() => {
    letterElement.textContent = finalChar;
  }, 300);
}

export type SubscriptionPlanFeature = {
  text: string;
  available: boolean;
};

export type SubscriptionPlan = {
  id: string;
  name: string;
  priceMonthly?: number;
  priceYearly?: number;
  priceString: string;
  description: string;
  features: SubscriptionPlanFeature[];
  canvasColors: number[][];
  opacities?: number[];
  dotSize?: number;
  animationSpeed?: number;
  requiredAchievementTier?: string;
  notes?: string;
  badgeText?: string;
  isLimited?: boolean;
  glowEffect?: string;
};

export const SUBSCRIPTION_PLANS: SubscriptionPlan[] = [
  {
    id: 'core_access',
    name: 'Core Access',
    priceString: 'Free',
    description: "Users getting started with Techfolio",
    features: [
      { text: 'Engage with 3 job postings per day', available: true },
      { text: '1 System Design Interview attempt per month', available: true },
      { text: '3 featured projects', available: true },
      { text: 'A direct, sharable link to your profile and featured work—no login required.', available: true },
      { text: 'Profile Analytics', available: false },
      { text: 'Unlock "Message First" functionality', available: false },
      { text: '"Founding Engineer" badge', available: false },
    ],
    canvasColors: [[100, 100, 100], [120, 120, 120]],
    opacities: [0.1, 0.1, 0.1, 0.2, 0.2, 0.2, 0.3, 0.3, 0.3, 0.5],
    dotSize: 2,
    animationSpeed: 0.4,
  },
  {
    id: 'pro_builder',
    name: 'Pro Builder',
    priceMonthly: 50,
    priceYearly: 500,
    priceString: '$50/month',
    description: "Active job seekers who want more reach and engagement",
    badgeText: 'Pro Builder',
    features: [
      { text: 'Engage with 5 job postings per day', available: true },
      { text: '2 System Design Interview attempts per month', available: true },
      { text: '5 featured projects', available: true },
      { text: 'A direct, sharable link to your profile and featured work—no login required.', available: true },
      { text: 'Profile Analytics (total profile views)', available: true },
      { text: 'Unlock "Message First" functionality', available: false },
      { text: '"Founding Engineer" badge', available: false }
    ],
    canvasColors: [[59, 130, 246], [139, 92, 246]],
    opacities: [0.2, 0.2, 0.2, 0.2, 0.2, 0.4, 0.4, 0.4, 0.4, 1],
    dotSize: 2,
    animationSpeed: 0.5,
  },
  {
    id: 'founding_candidate',
    name: 'Founding Engineer Edition',
    priceYearly: 250,
    priceString: '$250/year',
    description: "Early adopters and high-intent candidates aiming for elite visibility and employer engagement",
    badgeText: 'Founding Engineer',
    features: [
      { text: 'Engage with 10 job postings per day', available: true },
      { text: '3 System Design Interview attempts per month', available: true },
      { text: '10 featured projects', available: true },
      { text: 'A direct, sharable link to your profile and featured work—no login required.', available: true },
      { text: 'Advanced Analytics (Profile visits, projects and challenges viewed, companies that visited your profile) ', available: true },
      { text: 'Message First After Match (Optional)', available: true },
      { text: 'Access to "Founding Engineer" badge on profile', available: true },
    ],
    canvasColors: [[255, 165, 0], [255, 215, 0]],
    opacities: [0.3, 0.3, 0.3, 0.5, 0.5, 0.5, 0.8, 0.8, 0.8, 1],
    dotSize: 2,
    animationSpeed: 0.6,
    notes: "Limited availability: This tier is for our pioneering users.",
    isLimited: true,
    glowEffect: 'shadow-[0_0_15px_5px_rgba(255,215,0,0.5)]',
  },
];